/**
 * Document template placeholder catalog.
 *
 * Enumerates every `{{path.to.field}}` token that the two fixed document
 * templates — the agency campaign proposal and the influencer × host
 * campaign contract — may reference at render time. The admin editor
 * (task 8) surfaces these as a copy-clickable side panel so template
 * authors know exactly which paths resolve against the runtime context
 * built by the PDF generators.
 *
 * Coverage rules (Req 7.1–7.5):
 * - Every path used by the starter HTML in
 *   `backend/src/lambdas/documentTemplates/index.ts` MUST appear here.
 * - Every path referenced by the live generators in
 *   `frontend/src/utils/campaignProposalPdf.ts` and
 *   `frontend/src/utils/campaignContractPdf.ts` SHOULD appear here.
 * - `description` is a short Korean phrase stored directly as the user
 *   copy — the primary user language for this product is Korean and
 *   the editor displays these strings verbatim.
 * - No path is duplicated within a doctype list.
 */

/** The two fixed document types managed by this feature. */
export type DocType =
  | 'agency_campaign_proposal'
  | 'influencer_host_campaign_contract';

/** A single placeholder entry shown in the editor's side panel. */
export interface PlaceholderDescriptor {
  /** Dotted path matching the `{{...}}` token, e.g. `campaign.brandName`. */
  path: string;
  /** Short Korean label shown next to the token in the editor UI. */
  description: string;
}

export const DOCUMENT_TEMPLATE_PLACEHOLDERS: Record<DocType, PlaceholderDescriptor[]> = {
  agency_campaign_proposal: [
    // 1. Campaign basic info
    { path: 'campaign.brandName', description: '브랜드명' },
    { path: 'campaign.campaignName', description: '캠페인명' },
    { path: 'campaign.campaignCategory', description: '캠페인 카테고리' },
    { path: 'campaign.clientBrandName', description: '클라이언트 브랜드' },

    // 2. Product / service info
    { path: 'campaign.productIntroduction', description: '제품/서비스 소개' },
    { path: 'campaign.productName', description: '제공 제품/서비스명' },
    { path: 'campaign.providedValueDescription', description: '제품/서비스 가치' },
    { path: 'campaign.provisionMethod', description: '제공 방식' },
    { path: 'campaign.visitAddress', description: '방문 주소' },
    { path: 'campaign.additionalNotes', description: '방문 및 안내사항' },

    // 3. Campaign mission
    { path: 'campaign.numberOfRecruits', description: '모집인원' },
    { path: 'campaign.contentChannelsDisplay', description: '콘텐츠 발행 채널' },
    { path: 'campaign.rewardAmount', description: '리워드(제작지원비)' },
    { path: 'campaign.influencerPayoutAmount', description: '인플루언서 정산 금액(80%)' },
    { path: 'campaign.publishingConditionsSummary', description: '발행조건 요약' },
    { path: 'campaign.contentInsertLink', description: '콘텐츠 삽입 링크' },

    // 4. Scheduling
    { path: 'campaign.recruitmentPeriod.startDate', description: '모집 시작일' },
    { path: 'campaign.recruitmentPeriod.endDate', description: '모집 종료일' },
    { path: 'campaign.announcementStartDate', description: '발표 시작일' },
    { path: 'campaign.announcementEndDate', description: '발표 종료일' },
    { path: 'campaign.visitStartDate', description: '방문 시작일' },
    { path: 'campaign.visitEndDate', description: '방문 종료일' },
    { path: 'campaign.uploadDeadlineDays', description: '업로드 기한(일)' },

    // 5. Content guide & secondary submissions
    { path: 'campaign.contentWritingRequirements', description: '콘텐츠 작성 가이드' },
    { path: 'campaign.secondarySubmissionsSummary', description: '2차 활용 제출 항목' },
    { path: 'campaign.submissionPath', description: '제출 경로' },
    { path: 'campaign.secondarySubmissionGuide', description: '2차 활용 제출 가이드' },

    // 6. Document metadata
    { path: 'campaign.proposalDate', description: '제안서 작성일' },
    { path: 'campaign.proposalGeneratedAt', description: '제안서 생성 타임스탬프' },
  ],

  influencer_host_campaign_contract: [
    // Campaign fields referenced in the contract body
    { path: 'campaign.brandName', description: '브랜드명' },
    { path: 'campaign.campaignName', description: '캠페인명' },
    { path: 'campaign.productName', description: '제공 제품/서비스명' },
    { path: 'campaign.providedValueDescription', description: '제품/서비스 가치' },
    { path: 'campaign.influencerRewardAmount', description: '인플루언서 리워드' },
    { path: 'campaign.uploadDeadlineDays', description: '업로드 기한(일)' },
    { path: 'campaign.missionsSummary', description: '미션 요약' },
    { path: 'campaign.secondarySubmissionsSummary', description: '2차 제출 요약' },
    { path: 'campaign.contentWritingRequirements', description: '콘텐츠 가이드라인' },

    // Influencer identity
    { path: 'influencer.name', description: '인플루언서 이름' },

    // Match lifecycle
    { path: 'match.visitStartDate', description: '방문 시작일' },
    { path: 'match.visitEndDate', description: '방문 종료일' },
    { path: 'match.requestedAt', description: '요청 일시' },
    { path: 'match.respondedAt', description: '응답 일시' },
  ],
};
